// One conversation, decrypted in this browser with its device key, with the switch to show
// what the host stores of it instead.
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { Switch } from "rizzui";

import { conversationQuery } from "../api/queries";
import { conversationKind } from "../conversation/display";
import { EventList } from "../conversation/EventList";
import { ShowWorkingSwitch } from "../conversation/ShowWorkingSwitch";
import { HostRecord } from "../conversation/HostRecord";
import { useConversation } from "../conversation/useConversation";
import { type DeviceState, useDevice } from "../device/context";
import { useT } from "../i18n";
import { useRelayStatus } from "../relay/useRelayStatus";
import { cardClass, Field, WidgetCard } from "../ui/Card";
import { cn } from "../ui/cn";
import { PageHeader } from "../ui/PageHeader";
import { ErrorState, Loading } from "../ui/states";
import { E2EEBadge } from "../ui/status";
import { dayTime } from "../workloads/format";
import { LinkFirst } from "./Conversations";
import { shortId } from "./format";
import { detailCrumbs } from "../layout/nav";

export default function ConversationDetailPage() {
  const { conversation: t } = useT();
  const { conversationId = "" } = useParams();
  const device = useDevice();
  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {t.conversation} <E2EEBadge />
          </span>
        }
        breadcrumb={detailCrumbs("conversations", "/conversations", { name: shortId(conversationId) })}
      />
      {device.kind === "loading" ? <Loading label={t.checkingKeys} /> : null}
      {device.kind === "unlinked" ? <LinkFirst /> : null}
      {/* Keyed so the decrypted state restarts when the route changes conversations. */}
      {device.kind === "linked" ? <LinkedConversation key={conversationId} conversationId={conversationId} device={device} /> : null}
    </>
  );
}

function LinkedConversation({ conversationId, device }: { conversationId: string; device: DeviceState & { kind: "linked" } }) {
  const t = useT().conversation;
  const d = t.detail;
  const detail = useQuery(conversationQuery(conversationId, true));
  const { state } = useConversation(device, conversationId);
  const relay = useRelayStatus(device.relay);
  const [hostView, setHostView] = useState(false);
  const meta = detail.data?.conversation;
  return (
    <div className="space-y-5">
      <section className={cn(cardClass, "grid grid-cols-2 gap-x-6 gap-y-4 p-5 md:grid-cols-3 xl:grid-cols-6")} aria-label={d.facts}>
        <Field label={t.columns.conversation} mono>
          <span title={conversationId}>{shortId(conversationId)}</span>
        </Field>
        <Field label={t.columns.kind}>{meta ? conversationKind(meta.kind) : "—"}</Field>
        <Field label={t.columns.about}>
          {meta?.run_id ? <Link to={`/runs/${meta.run_id}`}>{t.aboutRun(shortId(meta.run_id))}</Link> : meta?.workspace_id ? t.aboutWorkspace(shortId(meta.workspace_id)) : "—"}
        </Field>
        <Field label={t.columns.created}>{meta ? dayTime(meta.created_at) : "—"}</Field>
        <Field label={d.relay}>
          <span data-testid="relay-status">{t.relayStatus[relay]}</span>
        </Field>
        <Field label={d.worker}>
          <span data-testid="presence">{state.live ? d.connected : d.offline}</span>
        </Field>
      </section>
      {detail.isError ? <ErrorState message={detail.error.message} /> : null}
      <WidgetCard
        label={d.events}
        title={d.events}
        description={d.eventsDescription}
        action={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {!hostView ? <ShowWorkingSwitch /> : null}
            <Switch role="switch" size="sm" label={t.showHost} labelClassName="text-[13px] text-gray-700" checked={hostView} onChange={() => setHostView((v) => !v)} />
          </span>
        }
      >
        {hostView ? <HostRecord state={state} /> : <EventList state={state} />}
      </WidgetCard>
    </div>
  );
}
